// src/components/Admin.js

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  IconButton,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { useAuthToken } from '../hooks/useAuthToken';
import { apiConfig } from '../config/auth.config';

const Admin = () => {
  const { user, isAuthenticated } = useAuth0();
  const { getToken } = useAuthToken();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newIndexerKey, setNewIndexerKey] = useState('');
  const [updatingUser, setUpdatingUser] = useState(null);
  
  // Get user's role from the token claims
  const userRole = user?.['https://indexerglue.com/role'] || 'user';
  const isAdmin = userRole === 'admin';

  const fetchUsers = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${apiConfig.baseUrl}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [getToken, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [isAdmin, fetchUsers]);

  const handleUpdateIndexerKey = async (userId, indexerId) => {
    if (!newIndexerKey.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an indexer key',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setUpdatingUser(userId);
    try {
      const token = await getToken();
      const response = await fetch(`${apiConfig.baseUrl}/admin/update_indexer_key`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          indexer_id: indexerId,
          new_api_key: newIndexerKey,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update indexer key');
      }

      toast({
        title: 'Success',
        description: 'Indexer key updated successfully',
        status: 'success',
        duration: 3000,
      });
      
      setNewIndexerKey('');
      onClose();
      fetchUsers();
    } catch (error) {
      console.error('Error updating indexer key:', error);
      toast({
        title: 'Error',
        description: 'Failed to update indexer key',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleUpdateUserRole = async (userId, newRole, newPlan) => {
    setUpdatingUser(userId);
    try {
      const token = await getToken();
      const response = await fetch(`${apiConfig.baseUrl}/admin/update_user`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          role: newRole,
          plan: newPlan,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      toast({
        title: 'Success',
        description: 'User updated successfully',
        status: 'success',
        duration: 3000,
      });
      
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: 'Error',
        description: 'Failed to update user',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setUpdatingUser(null);
    }
  };

  const openUpdateKeyModal = (user, indexer) => {
    setSelectedUser({ user, indexer });
    setNewIndexerKey('');
    onOpen();
  };

  // Access denied for non-admins
  if (!isAdmin) {
    return (
      <Box p={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Access denied. Admin privileges required.
        </Alert>
        <Text mt={4} color="gray.600">
          Your current role: <Badge colorScheme="gray">{userRole}</Badge>
        </Text>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" color="teal.500" />
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Heading size="lg" mb={6}>Admin Dashboard</Heading>
      
      <Alert status="info" mb={6} borderRadius="md">
        <AlertIcon />
        Indexer API keys are masked for security. You can update keys but not view existing values.
      </Alert>

      {/* Users Table */}
      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Plan</Th>
              <Th>Indexers</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((u) => (
              <Tr key={u.user_id}>
                <Td>
                  <Text fontWeight="medium">{u.email}</Text>
                  <Text fontSize="xs" color="gray.500">{u.user_id}</Text>
                </Td>
                <Td>
                  <Select
                    size="sm"
                    value={u.role || 'user'}
                    onChange={(e) => handleUpdateUserRole(u.user_id, e.target.value, u.plan)}
                    isDisabled={updatingUser === u.user_id}
                    width="100px"
                  >
                    <option value="user">User</option>
                    <option value="premium">Premium</option>
                    <option value="admin">Admin</option>
                  </Select>
                </Td>
                <Td>
                  <Select
                    size="sm"
                    value={u.plan || 'free'}
                    onChange={(e) => handleUpdateUserRole(u.user_id, u.role, e.target.value)}
                    isDisabled={updatingUser === u.user_id}
                    width="100px"
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                  </Select>
                </Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    {u.indexers && u.indexers.length > 0 ? (
                      u.indexers.map((indexer) => (
                        <HStack key={indexer.indexer_id} spacing={2}>
                          <Badge colorScheme="blue" fontSize="xs">
                            {indexer.indexer_name}
                          </Badge>
                          <Text fontSize="xs" color="gray.500">
                            ••••••••{indexer.api_key_hint || '****'}
                          </Text>
                          <Button
                            size="xs"
                            colorScheme="orange"
                            variant="outline"
                            onClick={() => openUpdateKeyModal(u, indexer)}
                          >
                            Update Key
                          </Button>
                        </HStack>
                      ))
                    ) : (
                      <Text fontSize="xs" color="gray.400">No indexers</Text>
                    )}
                  </VStack>
                </Td>
                <Td>
                  {updatingUser === u.user_id && <Spinner size="sm" />}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {users.length === 0 && (
        <Box textAlign="center" py={10}>
          <Text color="gray.500">No users found</Text>
        </Box>
      )}

      {/* Update Indexer Key Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Indexer Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedUser && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="medium">User:</Text>
                  <Text color="gray.600">{selectedUser.user.email}</Text>
                </Box>
                <Box>
                  <Text fontWeight="medium">Indexer:</Text>
                  <Badge colorScheme="blue">{selectedUser.indexer.indexer_name}</Badge>
                </Box>
                <FormControl>
                  <FormLabel>New API Key</FormLabel>
                  <Input
                    type="password"
                    placeholder="Enter new API key"
                    value={newIndexerKey}
                    onChange={(e) => setNewIndexerKey(e.target.value)}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    The existing key will be replaced. This cannot be undone.
                  </Text>
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={() => handleUpdateIndexerKey(
                selectedUser.user.user_id,
                selectedUser.indexer.indexer_id
              )}
              isLoading={updatingUser === selectedUser?.user.user_id}
            >
              Update Key
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Admin;
